import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import styled from 'styled-components';
import {useDispatch, useSelector} from 'react-redux';

import {media} from '~/shared/theme/media';
import {MoneyCard} from '~/shared/services/bffService/api/interfaces/responses';
import {usePersonalAreaContext} from '~/personalArea/context';
import TenbisCardsList from '~/personalArea/components/Sections/MyTenbis/TenbisCardsList';
import {selectUserData} from '~/shared/store/storeModules/user';
import {SimpleSpinnerLoader} from '~/shared/components/Loaders';
import {LoaderContainer} from '~/personalArea/components/Sections/Shared';
import {setCurrentModal} from '~/shared/store/storeModules/currentModal/currentModalActions';
import {trackEvent} from '~/shared/services/analytics';
import {revealMoneycard} from '~/shared/services/bffService/api';
import {createLogger} from '~/shared/logging';
import usePhoneVerification from '~/shared/hooks/usePhoneVerification';
import {ApiError} from '~/shared/services/apiErrorService';
import {useIsMinLargeMobile} from '~/shared/hooks/deviceInfo';

import TenbisCardView from './TenbisCardView';

const logger = createLogger('MyTenbis');

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 16px;
  gap: 24px;
  ${media.minLargeTablet`
    padding: 40px;
  `}
`;

const MyTenbis = () => {
  const timeoutRef = useRef<NodeJS.Timeout>();
  const [currentMoneycard, setCurrentMoneycard] = useState<MoneyCard>();
  const [cardFullNumber, setCardFullNumber] = useState<string | null>(null);
  const {isLoading, moneycards: allCards, activateMoneyCardById, getUserPayments} = usePersonalAreaContext();
  const userData = useSelector(selectUserData);
  const dispatch = useDispatch();
  const {getUserCurrentSessionToken} = usePhoneVerification();
  const isMinLargeMobile = useIsMinLargeMobile();

  const onActivateMoneycardClick = (id: string) => {
    trackEvent('mcActivate_click');
    dispatch(setCurrentModal('pieActivateMoneycardModal', {
      onSuccess: () => {
        setCurrentMoneycard(mc => (mc && mc.id === id ? {...mc, isActivated: true} : mc));
        activateMoneyCardById(id);
      },
    }));
  };

  const verifyUser = useCallback((onSuccess: (id: string) => void) => {
    if (!userData?.email) {
      logger.warn('No email in user data! Cant verify the user');
      return;
    }
    if (!currentMoneycard) {
      logger.warn('No moneycard to get details for');
      return;
    }
    dispatch(setCurrentModal('phoneVerificationModal', {
      email: userData.email,
      onSuccess: () => onSuccess(currentMoneycard.id),
    }));
  }, [userData, currentMoneycard, dispatch]);

  const getMoneyCardDetails = useCallback(async (id: string) => {
    try {
      const {mcFullNumber} = await revealMoneycard(id);
      const chunkedMcFullNumber = mcFullNumber.match(/\d{4}/g);
      if (!chunkedMcFullNumber) {
        logger.warn('MoneyCard number is malformed');
        return;
      }
      setCardFullNumber(chunkedMcFullNumber.join('-'));
    } catch (err) {
      if (err instanceof ApiError && err.status === 428) {
        verifyUser(getMoneyCardDetails);
        return;
      }
      logger.warn('Something went wrong');
    }
  }, [verifyUser]);

  const onShowCardDetailsClick = useCallback(async () => {
    if (!currentMoneycard) {
      logger.warn('No moneycard to get details for');
      return;
    }
    
    if (!getUserCurrentSessionToken()) {
      verifyUser(getMoneyCardDetails);
      return;
    }

    await getMoneyCardDetails(currentMoneycard.id);
  }, [currentMoneycard, verifyUser, getMoneyCardDetails, getUserCurrentSessionToken]);

  const hideDetails = useCallback(() => {
    setCardFullNumber(null);
  }, []);

  const onMoneycardClick = useCallback((moneycard: MoneyCard) => {
    setCurrentMoneycard(moneycard);
  }, []);

  const onBackButtonClick = useCallback(() => setCurrentMoneycard(undefined), []);

  const moneycards = useMemo(() => allCards?.filter(moneycard => !moneycard.isTenbisCredit) || [], [allCards]);

  useEffect(() => {
    if (!currentMoneycard && !isLoading && moneycards?.length === 1) {
      setCurrentMoneycard(moneycards[0]);
    }
  }, [isLoading, moneycards, currentMoneycard]);

  useEffect(() => {
    if (cardFullNumber) {
      timeoutRef.current = setTimeout(() => {
        hideDetails();
      }, 60000);
    }

    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, [hideDetails, cardFullNumber]);

  if (isLoading) {
    return (
      <LoaderContainer>
        <SimpleSpinnerLoader variant="pieBig" />
      </LoaderContainer>
    );
  }

  return (
    <Root>
      {currentMoneycard && (
        <TenbisCardView
          currentMoneycard={currentMoneycard}
          cardFullNumber={cardFullNumber}
          refetchPayments={getUserPayments}
          onBackButtonClick={moneycards.length > 1 ? onBackButtonClick : null}
          userData={userData}
          onActivateMoneycardClick={onActivateMoneycardClick}
          onShowDetailsClick={onShowCardDetailsClick}
          onHideDetailsClick={hideDetails}
        />
      )}
      {!currentMoneycard && !!moneycards && !!moneycards.length && (
        <TenbisCardsList
          moneycards={moneycards}
          onMoneycardClick={onMoneycardClick}
          onActivateMoneycardClick={onActivateMoneycardClick}
          isMinLargeMobile={isMinLargeMobile}
        />
      )}
    </Root>
  );
};

export default MyTenbis;
